import { useMutation, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import toastr from 'react-hot-toast';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  ChatMessageAttribute,
  ChatMessageResponse,
  SingleChatMessageResponse,
} from 'types/Chats/ChatMessage';

type MutationVariables = Partial<ChatMessageAttribute> & { messageID?: string };

export const useUpdateChatMessage = (
  messageID?: string,
  chatID?: string | undefined
) => {
  const queryClient = useQueryClient();

  const updateChatMessage = async (variables: MutationVariables) => {
    const { messageID: messageVariableID, ...restVariables } = variables;
    const id = messageVariableID || messageID;
    if (!id) {
      return Promise.reject('messageID not found');
    }

    const body = {
      data: {
        id,
        type: SPARK_RESOURCE_TYPES.CHAT_MESSAGES,
        attributes: restVariables,
      },
    };
    const url = generateURL(SPARK_RESOURCE_TYPES.CHAT_MESSAGES, [], id);
    return fetchWrapper<SingleChatMessageResponse>({
      url: url,
      method: 'PATCH',
      body,
    });
  };

  return useMutation('updateChatMessage', updateChatMessage, {
    onMutate: (variables) => {
      queryClient.setQueryData<ChatMessageResponse | undefined>(
        [SPARK_RESOURCE_TYPES.CHAT_MESSAGES, chatID],
        (old) => {
          const newChatMessages = old?.data.map((chatMessage) => {
            if (chatMessage.id === variables.messageID) {
              console.log(
                'updateChatMessage',
                variables.messageID,
                variables.chatMessageRatingTypeID
              );
              return {
                ...chatMessage,
                attributes: {
                  ...chatMessage.attributes,
                  chatMessageRatingTypeID: variables.chatMessageRatingTypeID,
                },
              };
            }
            return chatMessage;
          });
          if (!newChatMessages) {
            return old;
          }
          return {
            ...old,
            data: newChatMessages,
          };
        }
      );

      // Update infinite query data
      queryClient.setQueryData<
        { pages: ChatMessageResponse[]; pageParams: any[] } | undefined
      >(
        [SPARK_RESOURCE_TYPES.CHAT_MESSAGES, chatID, 'paginated', 10],
        (old) => {
          if (!old?.pages || old.pages.length === 0) return old;

          const updatedPages = old.pages.map((page, index) => {
            if (index === 0) {
              const updatedData = page.data.map((chatMessage) => {
                if (chatMessage.id === variables.messageID) {
                  return {
                    ...chatMessage,
                    attributes: {
                      ...chatMessage.attributes,
                      chatMessageRatingTypeID:
                        variables.chatMessageRatingTypeID,
                    },
                  };
                }
                return chatMessage;
              });
              return {
                ...page,
                data: updatedData,
              };
            }

            return page;
          });

          return {
            ...old,
            pages: updatedPages,
          };
        }
      );
    },
    onError: (error) => {
      if (error instanceof Error) {
        toastr.error(`Failed to update Chat message: ${error.message}`);
      }
    },
  });
};
